/**
 * Library of functions to help with displaying alert banners
 */
const A11yAlerts = (function () {
    const _AUTO_DISMISS_DURATION = 20000;     // 20 seconds

    //  Enum of alert banner types
    const _ALERT_TYPE = {
        ERROR: 'alert-danger',
        INFO: 'alert-info',
        SUCCESS: 'alert-success',
        WARNING: 'alert-warning'       
    };

    const _ALERT_MSG = {
        GENERIC_ERROR: 'Review the highlighted items before proceeding.',
        ERROR_OCCURRED: 'An error occurred, please try again.',
        NO_RECORDS_FOUND: 'No records found.',
        SELECT_RECORD_NOTES_HISTORY: 'Please select a record to see Notes History.',
        SELECT_RECORD_EDIT: 'Please select a record to edit.',
        SELECT_RECORD_DELETE: 'Please select at least one record to delete.',
        SELECT_RECORD_RECONCILE: 'Please select a record to be reconciled.',
        SELECT_RECORD_VIEW: 'Please select a record to view.'
    };

    return {
        AUTO_DISMISS_DURATION: _AUTO_DISMISS_DURATION,
        ALERT_MSG: _ALERT_MSG,
        ALERT_TYPE: _ALERT_TYPE,
        create: _create,
        createErrorAlert: _createErrorAlert,
        createFormValidationErrorAlert: _createFormValidationErrorAlert,
        createInfoAlert: _createInfoAlert,
        createSuccessAlert: _createSuccessAlert,
        createWarningAlert: _createWarningAlert,
        remove: _remove       
    };

    /**
     * Create a new alert. This will remove any existing alert and create a brand new one. Alerts will stay visible until dismissed.
     * @param {string} containerSelector CSS selector representing where this alert should be added (it will be added just inside the element, before other inner elements)
     * @param {string} alertMessage text to display in the alert message
     * @param {object} [configObj] optional configuration object allowing you to set various properties
     *                 configObj.alertType type of alert to show, from ALERT_TYPE enum (default: A11yAlerts.ALERT_TYPE.ERROR)
     *                 configObj.hideCloseButton flag indicating you want to hide the close button on the alert banner (default: false)
     *                 configObj.isCompact flag indicating you want to show a more compact error message
     *                 configObj.doNotStealFocus flag indicating you do NOT want to set the focus to the alert banner close button when it gets displayed
     *                 configObj.keepOpen flag indicating you want to force the alert to remain open, even after A11yAlerts.remove() is called
     *                 configObj.isModal flag indicating the alert type as a modal and forcing some action to be selected
     *                 configObj.btnObject contains Id and text related to Alert Buttons. Eg: btnObject: [{ btnId: 'btnAuthEntryLeftNavYes', btnText: 'Yes, continue' }, {btnId: 'btnAuthEntryLeftNavNo', btnText: 'No, cancel' }]
     */
    function _create(containerSelector, alertMessage, configObj) {
        // hide an existing alert banner
        _remove(null, true);

        DevUtils.logDebug('Creating alert message in container [' + containerSelector + '] Message: ' + alertMessage, 'A11yAlerts.create()', configObj);

        let alertType = configObj && configObj.alertType ? configObj.alertType : _ALERT_TYPE.ERROR;
        let hideCloseButton = configObj && configObj.hideCloseButton ? configObj.hideCloseButton : false;
        let doNotStealFocus = configObj && configObj.doNotStealFocus ? configObj.doNotStealFocus : false;
        let keepOpen = configObj && configObj.keepOpen ? configObj.keepOpen : false;
        let isCompact = false;
        let isModal = configObj && configObj.isModal ? configObj.isModal : false;
        let btnObject = configObj && configObj.btnObject ? configObj.btnObject : [];

        let container = $(containerSelector);
        let alertBanner = $(_getBannerHtml(isModal));
        let alert = $(alertBanner).find('.alert');
        let btnHtml = _getAlertButtonsHtml(btnObject);

        if (configObj && configObj.isCompact) {
            isCompact = configObj.isCompact;
        } else {
            // if alert banner is inside a modal dialog and isCompact has not explicitly been set, automatically set isCompact to true
            let modalDialog = $(container).closest('.ah-modal');
            if (modalDialog && $(modalDialog).length > 0) {
                isCompact = true;
            }
        }

        $(alertBanner).find('.alert-msg').AhsSetHtml(alertMessage + btnHtml);
        $(alert).addClass(alertType);
        $(alertBanner).toggleClass('ah-sm-alert-banner', isCompact);

        if (keepOpen) {
            $(alertBanner).addClass('ah-alert-keep-open');
        }

        if (hideCloseButton) {
            $(alertBanner).find("button").not('.alertActionBtn').hide();
        }
        $(alertBanner).prependTo(container).slideDown(600, function () {
            if (!isModal && !hideCloseButton && !doNotStealFocus) {
                A11yUtil.setFocus($(alertBanner).find("button"));
            }
        });

        if (isModal && btnObject.length > 0) {
            var focusElements = '';
            btnObject.forEach((btn) => {
                focusElements += '#' + btn.btnId + ',';
            });
            focusElements += '#btnAlertClose,';
            focusElements = focusElements.slice(0, -1);
            A11yModal.trapFocusInModal(container[0], focusElements);
            A11yUtil.setFocus(btnObject[0].btnId, 900);//Adding delay greather that error field focus delay of 800
        }

        if (!keepOpen && alertType === _ALERT_TYPE.SUCCESS) {
            setTimeout(function () {
                _remove();
            }, _AUTO_DISMISS_DURATION);
        }
    }

    /**
     * Create an error alert message. This alternative to A11yAlerts.create() is a convenient way to display an error alert. It
     * will use a generic error message if none is provided
     * @param {string} containerSelector CSS selector representing where this alert should be added (it will be added just inside the element, before other inner elements)
     * @param {string} errorMessage optional text to display in the error message (default: 'Please correct the errors before proceeding')
     * @param {boolean} isCompact optional flag indicating you want to show a more compact error message (default: false)
     * @param {boolean} doNotStealFocus optional flag indicating you do NOT want to set the focus to the alert banner's close button once it is displayed
     * @param {boolean} keepOpen flag indicating you want to force the alert to remain open, even after A11yAlerts.remove() is called (default: false)
     */
    function _createErrorAlert(containerSelector, errorMessage, isCompact, doNotStealFocus, keepOpen) {
        errorMessage = errorMessage ? errorMessage : _ALERT_MSG.GENERIC_ERROR;
        _create(containerSelector, errorMessage, _createAlertConfigObj(isCompact, null, doNotStealFocus, keepOpen));
    }

    function _createFormValidationErrorAlert(containerSelector, errorMessage, isCompact, keepOpen) {
        errorMessage = errorMessage ? errorMessage : _ALERT_MSG.GENERIC_ERROR;
        _create(containerSelector, errorMessage, _createAlertConfigObj(isCompact, null, true, keepOpen));
    }

    /**
     * Create an information alert message. This alternative to A11yAlerts.create() is a convenient way to display an info alert
     * @param {string} containerSelector CSS selector representing where this alert should be added (it will be added just inside the element, before other inner elements)
     * @param {string} infoMessage text to display in the info message
     * @param {boolean} isCompact optional flag indicating you want to show a more compact error message (default: false)
     * @param {boolean} keepOpen flag indicating you want to force the alert to remain open, even after A11yAlerts.remove() is called (default: false)
     */
    function _createInfoAlert(containerSelector, infoMessage, isCompact, keepOpen) {
        _create(containerSelector, infoMessage, { alertType: _ALERT_TYPE.INFO, isCompact: isCompact, keepOpen: keepOpen });
    }

    /**
     * Create a success alert message. This alternative to A11yAlerts.create() is a convenient way to display a success alert
     * @param {string} containerSelector CSS selector representing where this alert should be added (it will be added just inside the element, before other inner elements)
     * @param {string} successMessage text to display in the success message
     * @param {boolean} isCompact optional flag indicating you want to show a more compact error message (default: false)
     * @param {boolean} keepOpen flag indicating you want to force the alert to remain open, even after A11yAlerts.remove() is called (default: false)
     */
    function _createSuccessAlert(containerSelector, successMessage, isCompact, keepOpen) {
        _create(containerSelector, successMessage, { alertType: _ALERT_TYPE.SUCCESS, isCompact: isCompact, keepOpen: keepOpen });
    }
   
    /**
     * Create a warning alert message. This alternative to A11yAlerts.create() is a convenient way to display a warning alert
     * @param {string} containerSelector CSS selector representing where this alert should be added (it will be added just inside the element, before other inner elements)
     * @param {string} warningMessage text to display in the warning message
     * @param {boolean} isCompact optional flag indicating you want to show a more compact error message (default: false)
     * @param {boolean} keepOpen flag indicating you want to force the alert to remain open, even after A11yAlerts.remove() is called (default: false)
     */
    function _createWarningAlert(containerSelector, warningMessage, isCompact, keepOpen) {
        _create(containerSelector, warningMessage, { alertType: _ALERT_TYPE.WARNING, isCompact: isCompact, keepOpen: keepOpen });
    }

    /**
     * Remove the alert (removes from DOM)
     * @param {string} containerId optional ID of container in which to remove the alert banner (in case you don't want to remove all of them)
     * @param {boolean} forceClose flag indicating you want to force close all alerts, even if keepOpen was true when creating the alert (default: false)
     */
    function _remove(containerId, forceClose) {
        DevUtils.logDebug('Removing alert banner', 'A11yAlerts.remove()', { containerId: containerId, forceClose: forceClose });
        let selector;

        if (containerId && !forceClose) {
            selector = '#' + containerId + ' .ah-alert-banner:not(.ah-alert-keep-open,.ah-multi-alert)';
        } else if (containerId) {
            selector = '#' + containerId + ' .ah-alert-banner:not(.ah-multi-alert)';
        } else if (!forceClose) {
            selector = '.ah-alert-banner:not(.ah-alert-keep-open,.ah-multi-alert)';
        } else {
            selector = '.ah-alert-banner:not(.ah-multi-alert)';
        }
        $(selector).remove();
    }

    ///////////////////////
    // private functions //
    ///////////////////////
    function _getBannerHtml(isModal) {
        var bannerHtml = '<div class="ah-alert-banner" ';
        bannerHtml += isModal ? 'role="dialog" aria-modal="true" aria-labelledby="Message dialog" ' : '';
        bannerHtml += 'style="display:none;">' +
            '       <div class="alert alert-dismissable">' +
            '           <div class="alert-icon" aria-hidden="true"></div>' +
            '           <span class="alert-msg" role="alert"></span>' +
            '           <button type="button" id="btnAlertClose" aria-label="close alert" class="close" onclick="A11yAlerts.remove(null, true);">' +
            '               <i class="fas fa-times" aria-hidden="true"></i>' +
            '           </button>' +
            '       </div>' +
            '   </div>';
        return bannerHtml;
    }

    ///////////////////////
    // private functions //
    ///////////////////////
    function _getAlertButtonsHtml(buttonObj) {
        var bannerAlertHtml = '';
        if (buttonObj && buttonObj.length > 0) {
            buttonObj.forEach(function (btn) {
                bannerAlertHtml +=
                    '<button id="' + btn.btnId + '" class="ah-btn-link f16 bold ItemML30 alertActionBtn" style="padding:0px !important">' + btn.btnText + '</button>';
            });
        }
        return bannerAlertHtml;
    }

    function _createAlertConfigObj(isCompact, hideCloseButton, doNotStealFocus, keepOpen) {
        let configObj = {};

        if (isCompact) {
            configObj.isCompact = isCompact;
        }

        if (hideCloseButton) {
            configObj.hideCloseButton = hideCloseButton;
        }

        if (doNotStealFocus) {
            configObj.doNotStealFocus = doNotStealFocus;
        }

        if (keepOpen) {
            configObj.keepOpen = keepOpen;
        }

        return configObj;
    }
})();

/**
 * Legacy Alert message functions
 *
 * OBSOLETE!!! - do not use these functions anymore, instead use one of the "create" functions within the A11yAlerts class above
 *
 * These functions dispay the old style alert messages in a popup dialog that disappears in a split second. Little by little, we should
 * convert these legacy alert use cases into a11y-compliant calls to A11yAlert.create().
 *
 * These have been migrated from GCHomeCommon.js so that we can consolidate alert messages in one JS file, and because GCHomeCommon.js is
 * just way too gigantic and Sonar complains about it.
 */

let LEGACY_ALERT_MODAL_ID = '';     // hold reference to the modal, so we can close it in a separate function
let LEGACY_ALERT_TRIGGER_BTN;       // hold reference to the button that triggered the alert message so we can refocus on it when alert is closed

/**
 * OBSOLETE - DO NOT USE!!!
 * This function is called by CustomAlert() in JS_CMN.js, a lot. A LOT. We need to replace calls to CustomAlert() any chance we get
*/
function legacyModalAlert(title, strMsg, close, callback, hideOk, timeout, triggerBtnId) {    // NOSONAR (unconventional function name, but there are too many usages to fix)
    // TEMPORARY CHANGE BACK TO DEFAULT 2.5 SECS UNTIL A BETTER LONG TERM SOLUTION IS APPLIED
    //timeout = 20000;   // alerts must display for at least 20 secs per a11y requirements
    timeout = (typeof timeout == "undefined" || timeout === "" || timeout == null) ? 2500 : timeout;
    DevUtils.logDebug('timeout set to ' + timeout, 'GCHomeCommon.legacyModalAlert()');

    let frameHtml = "<div style='width:600px;' role='alert'>" + strMsg + "</div>";
    title = title === "" ? "Message" : title;

    legacyAlertSetModalData({
        Title: title,
        Content: frameHtml,
        Close: close,
        CloseBack: callback,
        HideOk: hideOk,
        TimeOut: timeout,
        triggerBtnId: triggerBtnId
    });
}

function legacyAlertSetModalData(Options) {
    let Defaults = {
        Title: "",
        Content: "",
        Close: true,
        CloseBack: null,
        Width: "auto",
        Height: null,
        Top: null,
        HideOk: null,
        TimeOut: 2500,
        triggerBtnId: 'skipLink'
    };

    let _Options = $.extend(Defaults, Options);

    LEGACY_ALERT_MODAL_ID = "#CMModalPopupDivMessage";
    LEGACY_ALERT_TRIGGER_BTN = $('#' + _Options.triggerBtnId);

    let MpTitle = "<span style='text-transform:none !important;'>" + _Options.Title + "</span>";
    let MessageContent = "<span style='text-transform:none !important;'>" + _Options.Content + "</span>" + "<br/>" + "  <button type='button' id='BtnOkMessage' class='ah-btn ah-btn-primary small'>OK</button>   ";

    $("#CMModalPopup_HeadTitleMessage").html(MpTitle);
    $("#CMModalPopup_ContentMessage").html(MessageContent);
    $(LEGACY_ALERT_MODAL_ID).css({ 'width': 'auto' });

    // adjust sizing of the dialog
    let CurrWidth = $(LEGACY_ALERT_MODAL_ID).width();

    if (_Options.Width !== "auto") {
        CurrWidth = _Options.Width.toString();
        CurrWidth = CurrWidth.replace("px", "");
        $(LEGACY_ALERT_MODAL_ID).css({ 'width': CurrWidth });
    }

    let CurrLeft = parseInt($(window).width() / 2) - (parseInt(CurrWidth) / 2) + "px";
    $(LEGACY_ALERT_MODAL_ID).css({ 'left': CurrLeft });

    if (_Options.Height != null) {
        $("#CMModalPopup_ContentMessage").css({ 'height': _Options.Height.toString().replace("px", "") + "px" });
    }

    if (_Options.Top != null) {
        $(LEGACY_ALERT_MODAL_ID).css({ 'top': _Options.Top.toString().replace("px", "") + "px" });
    }

    // show the dialog
    $("#CMModalPopupMaskMessage").show();
    $(LEGACY_ALERT_MODAL_ID).slideDown('slow', function () {
        A11yModal.trapFocusInModal('CMModalPopupDivMessage');
    });

    if (_Options.HideOk != null) {
        $(LEGACY_ALERT_MODAL_ID).find('#CMModalPopup_HeadCloseMessage').show();
        $(LEGACY_ALERT_MODAL_ID).find('#BtnOkMessage').hide();
        A11yUtil.setFocus('CMModalPopup_HeadCloseBtnMessage');

        if ($('#PHIPNotificationsGrid').length > 0) {
            _Options.TimeOut = 5000;
        }

        DevUtils.logDebug('will close alert dialog in ' + _Options.TimeOut + ' ms', 'GCHomeCommon.legacyAlertSetModalData()');
        setTimeout(function () {
            $(LEGACY_ALERT_MODAL_ID).find('#BtnOkMessage').trigger("click");
        }, _Options.TimeOut);
    } else {
        $(LEGACY_ALERT_MODAL_ID).find('#BtnOkMessage').show();
        $(LEGACY_ALERT_MODAL_ID).find('#CMModalPopup_HeadCloseMessage').hide();
        A11yUtil.setFocus('BtnOkMessage');
    }

    if (_Options.Close) {
        $(LEGACY_ALERT_MODAL_ID).find('#BtnOkMessage').click(function () {
            legacyAlertModalClose(_Options.CloseBack);
            A11yUtil.setFocus(LEGACY_ALERT_TRIGGER_BTN);
        });
    }
}

function legacyAlertModalClose(McCallBack) {
    $(LEGACY_ALERT_MODAL_ID).slideUp("slow", function () {
        if ($('#popUpDialog1').length > 0 && $('#popUpDialog1').find('#closePopUp_1').length > 0) {
            $('#popUpDialog1').find('#closePopUp_1').attr('id', 'closePopUp');
        }

        $("#CMModalPopup_ContentMessage").html('');
        $("#CMModalPopupMaskMessage").hide();

        if (LEGACY_ALERT_TRIGGER_BTN) {
            A11yUtil.setFocus(LEGACY_ALERT_TRIGGER_BTN);
            LEGACY_ALERT_TRIGGER_BTN = null;
        }

        if (McCallBack && typeof (McCallBack) === "function") {
            McCallBack(true);
        }
        setTimeout(function () {
            resetOverlayScroll();
        }, 550);
    });
}

function smartCommMessageBoxAlert(title, strMsg, close, callback, hideOk) {
    title = title === "" ? "Message" : title;

    let errMsgs = strMsg.split("<br/>");
    let str = "<ul style='list-style-type: disc;'>";

    errMsgs.forEach(function (err) {
        str += "<li>" + err + "</li>";
    });

    str += "</ul>";

    let frameHtml = "<div style='width:600px; text-align: left; padding: 35px;'>" + str + "</div>";
    legacyAlertSetModalData({ Title: title, Content: frameHtml, Close: close, CloseBack: callback, HideOk: hideOk });
}
